import { cloneDeep, isEmpty } from 'lodash';
import dayjs from 'dayjs';
import { toDateTime } from '~/functions/dateTimeUtilities';
import type {
  AddNewGrantFormState,
  DisbursementDate,
  DisbursementFromAPI,
  GrantMemberFromAPI,
  LineItemState,
  LineItemFromAPI,
  Member,
  ReportDate,
  ReportFromAPI,
  SelectedDonorFromAPI,
  SelectedGrant,
  TableStatus
} from '~/types/grants';

export const addNewGrantBreadCrumb: { route: string; title: string }[] = [
  { route: '/grants', title: 'Grants' },
  { route: '/grants/add-new-grant', title: 'New Grant' }
];

export const grantMonthInitialAmounts: Record<string, number | null> = {
  april: null,
  may: null,
  june: null,
  july: null,
  august: null,
  september: null,
  october: null,
  november: null,
  december: null,
  january: null,
  february: null,
  march: null
};

export const grantMonths = [
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
  'january',
  'february',
  'march'
];

const monthNumbers: Record<string, number> = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12
};

const numberMonth: Record<number, string> = {
  1: 'january',
  2: 'february',
  3: 'march',
  4: 'april',
  5: 'may',
  6: 'june',
  7: 'july',
  8: 'august',
  9: 'september',
  10: 'october',
  11: 'november',
  12: 'december'
};

export enum GrantAttachmentCategories {
  GrantDocuments = 'Grant Documents',
  'Disbursement / Fund Request' = 'Disbursement / Fund Request',
  Reports = 'Reports',
  Formats = 'Formats',
  Proposals = 'Proposals',
  Others = 'Others'
}

export const formDefaultState: AddNewGrantFormState = {
  donorName: '',
  donorUid: null,
  grantId: '',
  signingDate: null,
  startDate: null,
  endDate: null,
  description: '',
  purpose: '',
  bankAccount: '',
  grantAmount: null,
  members: [],
  lineItems: [
    {
      key: Date.now(),
      name: '',
      amount: null,
      note: '',
      breakdownType: 'monthly',
      breakdowns: cloneDeep(grantMonthInitialAmounts),
      subItems: []
    }
  ],
  disbursementReminderEmailList: [],
  disbursementRemindBeforeDays: null,
  reportSendReminderTo: [],
  reportRemindBeforeDays: null,
  attachments: [],
  reportDatesTable: [{ key: Date.now(), dueDate: null, reportName: '', status: 'Pending' }],
  disbursementDatesTable: [{ key: Date.now(), dueDate: null, amount: null, status: 'Pending' }]
};

export const newGrantRules: {
  [key: string]: { required: boolean; message: string; trigger: string }[];
} = {
  donorName: [
    {
      required: true,
      message: 'Donor name is required!',
      trigger: 'submit'
    }
  ],
  grantId: [
    {
      required: true,
      message: 'Grant ID is required!',
      trigger: 'submit'
    }
  ],
  signingDate: [
    {
      required: true,
      message: 'Signing date is required!',
      trigger: 'submit'
    }
  ],
  startDate: [
    {
      required: true,
      message: 'Start date is required!',
      trigger: 'submit'
    }
  ],
  endDate: [
    {
      required: true,
      message: 'End date is required!',
      trigger: 'submit'
    }
  ],
  purpose: [
    {
      required: true,
      message: 'Purpose is required!',
      trigger: 'submit'
    }
  ],
  grantAmount: [
    {
      required: true,
      message: 'Grant amount is required!',
      trigger: 'submit'
    }
  ],
  members: [
    {
      required: false,
      message: 'Grant members are required!',
      trigger: 'submit'
    }
  ]
};

const grandMemberData = (members: Member[]) => {
  return members.reduce((acc, member) => {
    return acc + `${member.firstName}_${member.lastName}_${member.email}, `;
  }, '');
};

const buildCalendar = (itemNumber: number, breakdownType: string, breakdowns: Record<string, number | null>) => {
  const isCustom = breakdownType === 'custom';
  return {
    [`li_isCustom_${itemNumber}`]: isCustom ? 'true' : 'false',
    [`li_breakdown_${itemNumber}`]: Object.keys(breakdowns).reduce((acc: Record<string, string>, key) => {
      // For custom: include every month (null → "0") so all range months are stored.
      // For monthly: skip nulls as before.
      if (breakdowns[key] !== null || isCustom) {
        const numKey = monthNumbers[key]; // defined only for legacy lowercase month names
        if (numKey !== undefined) {
          // Legacy format: "april" -> store as month number "4"
          acc[String(numKey)] = String(breakdowns[key] ?? 0);
        } else {
          // New format: "Apr-25" -> store as-is
          acc[key] = String(breakdowns[key] ?? 0);
        }
      }
      return acc;
    }, {})
  };
};

const lineItemData = (lineItems: LineItemState[]) => {
  const formattedData: Record<string, any> = {};
  let itemNumber = 1;
  lineItems.forEach(item => {
    if (isEmpty(item.subItems)) {
      formattedData[itemNumber] = {
        [`li_text_${itemNumber}`]: item.name,
        [`li_subtext_${itemNumber}`]: '',
        [`li_amount_${itemNumber}`]: item.amount,
        [`li_note_${itemNumber}`]: item.note,
        [`li_calendar_${itemNumber}`]: buildCalendar(
          itemNumber,
          item.breakdownType || 'monthly',
          item.breakdowns || cloneDeep(grantMonthInitialAmounts)
        )
      };
      itemNumber++;
    } else {
      item.subItems.forEach(subItem => {
        formattedData[itemNumber] = {
          [`li_text_${itemNumber}`]: item.name,
          [`li_subtext_${itemNumber}`]: subItem.name,
          [`li_amount_${itemNumber}`]: subItem.amount,
          [`li_note_${itemNumber}`]: subItem.note || item.note,
          [`li_calendar_${itemNumber}`]: buildCalendar(
            itemNumber,
            subItem.breakdownType || 'monthly',
            subItem.breakdowns || cloneDeep(grantMonthInitialAmounts)
          )
        };
        itemNumber++;
      });
    }
  });
  return JSON.stringify(formattedData).toString();
};

export const createEmailList = (members: Member[]) => {
  return members.reduce((acc, member, index) => {
    if (index === members.length - 1) {
      return acc + member.email.trim();
    }
    return acc + `${member.email.trim()}, `;
  }, '');
};

const disbursementDateData = (disbursementDates: DisbursementDate[]) => {
  const formattedData: Record<string, any> = {};
  disbursementDates.forEach((disbursement, index) => {
    const itemNumber = index + 1;
    formattedData[itemNumber] = {
      [`dd_date_${itemNumber}`]: dayjs(disbursement.dueDate).format('YYYY-MM-DDTHH:mm:ss.SSS') + '000Z',
      [`dd_amount_${itemNumber}`]: disbursement.amount?.toString(),
      [`dd_status_${itemNumber}`]: disbursement.status
    };
  });
  return JSON.stringify(formattedData).toString();
};

const reportDateData = (reportDates: ReportDate[]) => {
  const formattedData: Record<string, any> = {};
  reportDates.forEach((date, index) => {
    const itemNumber = index + 1;
    formattedData[itemNumber] = {
      [`rd_date_${itemNumber}`]: dayjs(date.dueDate).format('YYYY-MM-DDTHH:mm:ss.SSS') + '000Z',
      [`rd_text_${itemNumber}`]: date.reportName,
      [`rd_status_${itemNumber}`]: date.status
    };
  });
  return JSON.stringify(formattedData).toString();
};

export const createFormData = (formState: AddNewGrantFormState) => {
  const formData: any = {
    donor_id: formState.donorUid?.toString() || '',
    grant_id: formState.grantId,
    start_date: formState.startDate?.toISOString(),
    end_date: formState.endDate?.toISOString(),
    signing_date: formState.signingDate?.toISOString(),
    description: formState.description,
    purpose: formState.purpose,
    grant_bank_account: formState.bankAccount || '',
    amount: formState.grantAmount?.toString(),
    grant_members: grandMemberData(formState.members),
    line_items: lineItemData(formState.lineItems),
    disbursement_emails: createEmailList(formState.disbursementReminderEmailList),
    disbursement_days_before: formState.disbursementRemindBeforeDays,
    disbursements_json: disbursementDateData(formState.disbursementDatesTable),
    reporting_emails: createEmailList(formState.reportSendReminderTo),
    reporting_days_before: formState.reportRemindBeforeDays,
    reports_json: reportDateData(formState.reportDatesTable)
  };
  formState.attachments.forEach((file, index) => {
    formData[`at_cat_${index + 1}`] = file.category;
    if (file.rawFile) {
      formData[`at_file_${index + 1}`] = file.rawFile;
    }
  });
  return formData;
};

const parseCalendar = (cal?: LineItemFromAPI['li_calendar']) => {
  const breakdownType: 'monthly' | 'custom' = cal?.li_isCustom === 'true' ? 'custom' : 'monthly';
  // Start with empty — will be filled with either legacy or new keys
  const breakdowns: Record<string, number | null> = {};
  if (cal?.li_breakdown) {
    Object.keys(cal.li_breakdown).forEach(key => {
      if (/^\d+$/.test(key)) {
        // Legacy format: numeric month-number key -> convert to lowercase month name
        const monthName = numberMonth[+key];
        if (monthName) breakdowns[monthName] = Number(cal.li_breakdown[key]) || null;
      } else {
        // New format: "Apr-25" or "april" -> keep as-is
        breakdowns[key] = Number(cal.li_breakdown[key]) || null;
      }
    });
  }
  return { breakdownType, breakdowns };
};

export const lineItemFormData = (lineItem: LineItemFromAPI[]) => {
  const lineItemArray: any[] = [];
  lineItem.forEach((item, index: number) => {
    const { breakdownType, breakdowns } = parseCalendar(item.li_calendar);
    const existingLineItem = lineItemArray.find(li => li.name === item.li_text);
    if (existingLineItem) {
      existingLineItem.subItems.push({
        key: Date.now() + index,
        name: item.li_subtext,
        amount: +item.li_amount,
        note: item.li_note || '',
        breakdownType,
        breakdowns: cloneDeep(breakdowns)
      });
    } else {
      if (item.li_subtext) {
        lineItemArray.push({
          key: Date.now() + index,
          name: item.li_text,
          amount: +item.li_amount,
          note: item.li_note || '',
          breakdownType,
          breakdowns: cloneDeep(breakdowns),
          subItems: [
            {
              key: Date.now(),
              name: item.li_subtext,
              amount: +item.li_amount,
              note: item.li_note || '',
              breakdownType,
              breakdowns: cloneDeep(breakdowns)
            }
          ]
        });
      } else {
        lineItemArray.push({
          key: Date.now() + index,
          name: item.li_text,
          amount: +item.li_amount,
          note: item.li_note || '',
          breakdownType,
          breakdowns: cloneDeep(breakdowns),
          subItems: []
        });
      }
    }
  });
  return lineItemArray;
};

export const createFormStateObject = (
  selectedGrant: SelectedGrant,
  selectedDonor: SelectedDonorFromAPI,
  reminderUserList: GrantMemberFromAPI[]
) => {
  const formState: AddNewGrantFormState = {
    donorUid: selectedGrant.donor_id,
    donorName: selectedDonor.name,
    grantId: selectedGrant.grant_id,
    startDate: toDateTime(selectedGrant.start_date),
    endDate: toDateTime(selectedGrant.end_date),
    signingDate: toDateTime(selectedGrant.signing_date),
    description: selectedGrant.description,
    purpose: selectedGrant.purpose,
    bankAccount: selectedGrant.grant_bank_account || '',
    grantAmount: +selectedGrant.amount,
    members: selectedGrant.grant_members
      .split(',')
      .map((member: string) => member.trim())
      .map((member: string) => {
        const [firstName, lastName, email] = member.trim().split('_', 3);
        return {
          firstName: firstName,
          lastName: lastName,
          email: email
        };
      }),
    lineItems: lineItemFormData(selectedGrant.line_items),
    disbursementDatesTable: selectedGrant.disbursements.map((data: DisbursementFromAPI, index: number) => ({
      key: Date.now() + index,
      dueDate: toDateTime(data.dd_date) || null,
      amount: Number(data.dd_amount) || null,
      status: data.dd_status as TableStatus
    })),
    reportDatesTable: selectedGrant.reports.map((report: ReportFromAPI, index: number) => ({
      key: Date.now() + index,
      dueDate: toDateTime(report.rd_date) || null,
      reportName: report.rd_text,
      status: report.rd_status as TableStatus
    })),
    disbursementReminderEmailList: selectedGrant.disbursement_emails
      .split(', ')
      .filter(Boolean)
      .map((email: string) => {
        const user = reminderUserList.find((user: GrantMemberFromAPI) => user.email === email);
        if (user) {
          return {
            firstName: user?.first_name,
            lastName: user?.last_name,
            email: user?.email
          };
        } else {
          return {
            firstName: null,
            lastName: null,
            email: email
          };
        }
      }),
    disbursementRemindBeforeDays: +selectedGrant.disbursement_days_before,
    reportSendReminderTo: selectedGrant.reporting_emails
      .split(', ')
      .filter(Boolean)
      .map((email: string) => {
        const user = reminderUserList.find((user: GrantMemberFromAPI) => user.email === email);
        if (user) {
          return {
            firstName: user?.first_name,
            lastName: user?.last_name,
            email: user?.email
          };
        } else {
          return {
            firstName: null,
            lastName: null,
            email: email
          };
        }
      }),
    reportRemindBeforeDays: selectedGrant.reporting_days_before,
    attachments:
      selectedGrant.attachments_data?.map((attachment: any, index: number) => ({
        key: Date.now() + index,
        filename: attachment.filename,
        category: attachment.category
      })) || []
  };
  return formState;
};

interface ValidationResult {
  tableError: string;
  isValid: boolean;
}

export const validateLineItems = (lineItems: LineItemState[], totalBudget?: number | null): ValidationResult => {
  for (const item of lineItems) {
    if (isEmpty(item.subItems)) {
      if (!item.name || item.amount === null || item.amount === undefined) {
        return { tableError: 'All fields in the table are required!', isValid: false };
      }
    } else {
      for (const subItem of item.subItems) {
        if (!subItem.name || subItem.amount === null || subItem.amount === undefined || !item.name) {
          return { tableError: 'All fields in the table are required!', isValid: false };
        }
      }
    }
  }

  if (totalBudget !== null && totalBudget !== undefined && totalBudget > 0) {
    // item.amount is kept in sync with sub-item sum by the component's watcher
    const total = lineItems.reduce((acc, item) => acc + (Number(item.amount) || 0), 0);
    const diff = Math.round((totalBudget - total) * 100) / 100;
    if (diff > 0) {
      return {
        tableError: `₹${diff.toLocaleString('en-IN')} is still left to be allocated in Line Items. Please allocate the full Grant Amount before submitting.`,
        isValid: false
      };
    }
    if (diff < 0) {
      return {
        tableError: `Line Items are over-allocated by ₹${Math.abs(diff).toLocaleString('en-IN')}. Please reduce the amounts to match the Grant Amount before submitting.`,
        isValid: false
      };
    }
  }

  return { tableError: '', isValid: true };
};

export const validateDisbursements = (
  disbursementDates: DisbursementDate[],
  totalBudget?: number | null
): ValidationResult => {
  for (const item of disbursementDates) {
    if (!item.dueDate || item.amount === null || item.amount === undefined) {
      return { tableError: 'All fields in the table are required!', isValid: false };
    }
  }

  if (totalBudget !== null && totalBudget !== undefined && totalBudget > 0) {
    const total = disbursementDates.reduce((acc, item) => acc + (Number(item.amount) || 0), 0);
    const diff = Math.round((totalBudget - total) * 100) / 100;
    if (diff > 0) {
      return {
        tableError: `₹${diff.toLocaleString('en-IN')} is still left to be allocated in Disbursement Dates. Please allocate the full Grant Amount before submitting.`,
        isValid: false
      };
    }
    if (diff < 0) {
      return {
        tableError: `Disbursement Dates are over-allocated by ₹${Math.abs(diff).toLocaleString('en-IN')}. Please reduce the amounts to match the Grant Amount before submitting.`,
        isValid: false
      };
    }
  }

  return { tableError: '', isValid: true };
};

export const validateReports = (reportDates: ReportDate[]): ValidationResult => {
  for (const item of reportDates) {
    if (!item.reportName) {
      return {
        tableError: 'All fields in the table are required!',
        isValid: false
      };
    }
  }
  return {
    tableError: '',
    isValid: true
  };
};
